import { RecentlyViewed } from '@theme/recently-viewed-products';
import { normalizeSectionId } from '@theme/section-renderer';
import { formatPrice } from '@theme/utilities';

/**
 * Custom web component for displaying recently viewed products
 */
class RecentlyViewedProducts extends HTMLElement {
  /**
   * The observer for lazy loading
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      const entry = entries[0];
      if (!entry?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Whether products have been loaded
   * @type {boolean}
   */
  #productsLoaded = false;

  /**
   * Re-apply responsive title/price layout on breakpoint changes.
   * This is important because recently viewed content can be injected after load,
   * and users/devtools can resize the viewport without reloading.
   */
  #mobileLayoutMediaQuery = window.matchMedia('(max-width: 749px)');

  #handleResponsiveLayoutChange = () => {
    // Apply to the whole component; safe even if products aren't loaded yet.
    this.#applyTitlePriceLayout(this);
  };

  connectedCallback() {
    // Keep title/price layout correct when resizing between breakpoints
    this.#mobileLayoutMediaQuery.addEventListener('change', this.#handleResponsiveLayoutChange);

    // Check if element is already visible (within viewport or close to it)
    const rect = this.getBoundingClientRect();
    const isAlreadyVisible = rect.top < window.innerHeight + 400; // 400px matches rootMargin

    if (isAlreadyVisible) {
      // Load immediately if already visible
      requestAnimationFrame(() => {
        this.#intersectionObserver.disconnect();
        this.#loadProducts();
      });
    } else {
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    this.#mobileLayoutMediaQuery.removeEventListener('change', this.#handleResponsiveLayoutChange);
  }

  /**
   * Read section settings from data attributes (set by sections/recently-viewed-products.liquid).
   * Fallbacks match the section schema defaults for when the component is used without the section.
   */
  #getSectionSettings() {
    return {
      sectionId: this.dataset.sectionId ?? '',
      maxProducts: this.dataset.maxProducts ?? '4',
      layoutType: this.dataset.layoutType ?? 'grid',
      carouselOnMobile: this.dataset.carouselOnMobile ?? 'false',
      columns: this.dataset.columns ?? '4',
      mobileColumns: this.dataset.mobileColumns ?? '2',
      columnsGap: this.dataset.columnsGap ?? '16',
      rowsGap: this.dataset.rowsGap ?? '16',
      avShowProductTypeWhenLessThanTwo: this.dataset.avShowProductTypeWhenLessThanTwo === 'true',
      avHideLabelWhenLessThanTwo: this.dataset.avHideLabelWhenLessThanTwo === 'true',
      avLabelSingular: this.dataset.avLabelSingular ?? 'variant',
      avLabelPlural: this.dataset.avLabelPlural ?? 'variants',
      avSuffix: this.dataset.avSuffix ?? 'available'
    };
  }

  /**
   * Returns the available-variants line for a product (for fallback/enhanced cards).
   * Mirrors blocks/available-variants.liquid logic using section settings.
   * @param {any} product - Product with variants (each variant may have .available)
   * @param {{ avShowProductTypeWhenLessThanTwo: boolean, avHideLabelWhenLessThanTwo: boolean, avLabelSingular: string, avLabelPlural: string, avSuffix: string }} avSettings
   * @returns {{ html: string, visible: boolean, preserveExistingContent?: boolean }}
   */
  #getAvailableVariantsForProduct(product, avSettings) {
    const root = product?.product ?? product;
    const variants = Array.isArray(root?.variants) ? root.variants : [];
    const availableCount = variants.filter((/** @type {{ available?: boolean }} */ v) => v && v.available).length;
    const label = availableCount === 1 ? avSettings.avLabelSingular : avSettings.avLabelPlural;
    const countText = `${availableCount} ${label} ${avSettings.avSuffix}`;

    if (avSettings.avHideLabelWhenLessThanTwo && availableCount < 2) {
      return { html: '', visible: false };
    }
    if (avSettings.avShowProductTypeWhenLessThanTwo && availableCount < 2) {
      const productTypeRaw =
        root?.type ??
        root?.product_type ??
        (typeof root?.Type === 'string' ? root.Type : undefined) ??
        (typeof root?.ProductType === 'string' ? root.ProductType : undefined);
      const productType = typeof productTypeRaw === 'string' ? productTypeRaw.trim() : '';
      if (productType) {
        return { html: this.#escapeHtml(productType), visible: true };
      }
      // Show product type is on but product JSON has no type – enforcement preserves server content; fallback shows count
      return { html: this.#escapeHtml(countText), visible: true, preserveExistingContent: true };
    }
    return { html: this.#escapeHtml(countText), visible: true };
  }

  /**
   * Load the recently viewed products
   */
  async #loadProducts() {
    if (this.#productsLoaded) return;

    const settings = this.#getSectionSettings();
    const { sectionId, maxProducts, layoutType, carouselOnMobile } = settings;
    const id = this.id;

    if (!id) {
      throw new Error('ID attribute is required');
    }
    // Get products from localStorage
    const productIds = RecentlyViewed.getProductIds();
    const productHandles = RecentlyViewed.getProductHandles();

    if (!productIds || productIds.length === 0) {
      this.#handleEmptyState();
      return;
    }

    // Exclude current product when section is on a PDP (by ID and by URL handle)
    const currentProductId = this.#getCurrentProductId();
    const currentHandle = this.#getCurrentHandleFromUrl();
    const filteredProductIds = productIds.filter((pid, index) => {
      // Exclude by product ID (e.g. from [data-product-id])
      if (currentProductId && String(pid).trim() === String(currentProductId).trim()) {
        return false;
      }
      // Exclude by handle when on a product URL (catches same product even if ID format differs)
      const handle = productHandles[index];
      if (currentHandle && handle && String(handle).trim() === String(currentHandle).trim()) {
        return false;
      }
      return true;
    });

    if (filteredProductIds.length === 0) {
      this.#handleEmptyState();
      return;
    }

    // Limit to max products and ensure handles align with IDs
    const limitedProductIds = filteredProductIds.slice(0, parseInt(maxProducts, 10));
    // Map handles to their corresponding IDs to ensure alignment
    const handleMap = new Map();
    productIds.forEach((id, index) => {
      if (filteredProductIds.includes(id) && productHandles[index]) {
        handleMap.set(id, productHandles[index]);
      }
    });
    const limitedProductHandles = limitedProductIds.map(id => handleMap.get(id) || null);

    // Try section rendering API first with product IDs and handles
    const sectionResult = await this.#fetchSectionWithProductIds(
      sectionId,
      limitedProductIds,
      limitedProductHandles,
      id
    );

    if (!sectionResult) {
      // Fallback: try with handles if available
      const validHandles = limitedProductHandles.filter((h) => typeof h === 'string' && h.trim().length);
      if (validHandles.length > 0) {
        const handleOk = await this.#fetchProductCardsByHandles(limitedProductIds, limitedProductHandles, layoutType, carouselOnMobile);
        if (handleOk) return;

        // Final fallback: simple JSON renderer
        const jsonOk = await this.#renderFallbackFromProductJson(validHandles, layoutType);
        if (jsonOk) return;
      }

      // Final fallback: hide the section
      this.#handleEmptyState();
    }
  }

  /**
   * Fetches product cards using handles via Section Rendering API
   * @param {string[]} productIds - Array of product IDs (for reference)
   * @param {string[]} productHandles - Array of product handles
   * @param {string} layoutType - Layout type (grid or carousel)
   * @param {string} carouselOnMobile - Whether to show carousel on mobile
   * @returns {Promise<boolean>} True if products were fetched successfully
   */
  async #fetchProductCardsByHandles(productIds, productHandles, layoutType, carouselOnMobile) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const productItems = [];

    // Create a map of handle to productId to ensure proper alignment
    const handleToIdMap = new Map();
    for (let i = 0; i < productIds.length && i < productHandles.length; i++) {
      const handle = productHandles[i];
      const productId = productIds[i];
      if (handle && typeof handle === 'string' && handle.trim() !== '' && productId) {
        handleToIdMap.set(handle, productId);
      }
    }

    const validHandles = Array.from(handleToIdMap.keys());

    // Fetch each product card individually
    for (let i = 0; i < validHandles.length; i++) {
      const handle = validHandles[i];
      const productId = handleToIdMap.get(handle) || '';

      try {
        // First, fetch product JSON to get all product data
        const jsonResponse = await fetch(`/products/${handle}.js`, {
          headers: { Accept: 'application/json' },
          signal: this.#activeFetch?.signal
        });

        if (!jsonResponse.ok) continue;

        const productData = await jsonResponse.json();
        if (!productData?.handle) continue;

        // Try to fetch the full product card using the recently-viewed-products section
        const { sectionId } = this.#getSectionSettings();
        if (sectionId) {
          const sectionUrl = new URL(window.location.href);
          sectionUrl.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
          sectionUrl.searchParams.set('product_ids', productId);
          sectionUrl.searchParams.set('product_handles', handle);

          const sectionResponse = await fetch(sectionUrl.toString(), { signal: this.#activeFetch?.signal });
          if (sectionResponse.ok) {
            const html = await sectionResponse.text();
            const fragment = document.createElement('div');
            fragment.innerHTML = html;

            // Find the product card in the section response
            let productCard = fragment.querySelector('.resource-list__item product-card') ||
                             fragment.querySelector('product-card') ||
                             fragment.querySelector('.resource-list__item');

            if (productCard) {
              const cardHtml = productCard.outerHTML || productCard.innerHTML;
              if (cardHtml?.trim().length > 0) {
                productItems.push({ id: productId, html: cardHtml });
                continue;
              }
            }
          }
        }

        // Fallback: try the minimal section-rendering-product-card (14px price, compare-at below sale on desktop)
        const cardParams = new URLSearchParams();
        cardParams.set('section_id', 'section-rendering-product-card');
        cardParams.set('price_compare_at_below_sale_desktop', '1');
        cardParams.set('price_compare_at_below_sale_desktop_gap', '2');
        const response = await fetch(`/products/${handle}?${cardParams.toString()}`, {
          signal: this.#activeFetch?.signal
        });

        if (response.ok) {
          const html = await response.text();
          const fragment = document.createElement('div');
          fragment.innerHTML = html;

          let productCard = fragment.querySelector('product-card') ||
                           fragment.querySelector('[data-product-id]') ||
                           fragment.querySelector('#shopify-section-section-rendering-product-card')?.querySelector('product-card, [data-product-id]');

          if (productCard) {
            const cardHtml = this.#enhanceProductCard(productCard.outerHTML || productCard.innerHTML, productData);
            if (cardHtml?.trim().length > 0) {
              productItems.push({ id: productId, html: cardHtml });
            }
          }
        }
      } catch (error) {
        // Silently continue to next product
      }
    }

    if (productItems.length > 0) {
      await this.#renderProducts(productItems, layoutType, carouselOnMobile);
      return true;
    }

    return false;
  }

  /**
   * Fetches the section with product IDs and handles
   * @param {string} sectionId - The section ID
   * @param {string[]} productIds - Array of product IDs
   * @param {string[]} productHandles - Array of product handles (may contain nulls)
   * @param {string} id - The component ID
   */
  async #fetchSectionWithProductIds(sectionId, productIds, productHandles, id) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const url = this.#buildSectionUrl(sectionId, productIds, productHandles || []);
      const response = await fetch(url, { signal: this.#activeFetch.signal });

      if (!response.ok) {
        if (!window.Shopify?.designMode) {
          this.#handleError(new Error(`Server returned ${response.status}`));
        }
        return false;
      }

      const html = await response.text();
      const fragment = document.createElement('div');
      fragment.innerHTML = html;

      // Try multiple selectors to find the section
      let section = fragment.querySelector(`recently-viewed-products[id="${id}"]`) ||
                   fragment.querySelector(`#shopify-section-${sectionId.replace(/^shopify-section-/, '')} recently-viewed-products`) ||
                   fragment.querySelector('recently-viewed-products');

      if (!section) {
        const normalizedSectionId = sectionId.replace(/^shopify-section-/, '');
        const wrapper = fragment.querySelector(`#shopify-section-${normalizedSectionId}`);
        if (wrapper) {
          section = wrapper.querySelector('recently-viewed-products') || wrapper;
        }
      }

      if (section) {
        const sectionHTML = section.outerHTML || section.innerHTML;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sectionHTML;
        const resourceList = tempDiv.querySelector('.resource-list');
        const productItems = tempDiv.querySelectorAll('.resource-list__item');
        const hasProductsAttr = resourceList?.getAttribute('data-has-products');

        if (hasProductsAttr === 'false' || productItems.length === 0) {
          return false;
        }

        this.#productsLoaded = true;

        // Sync attributes from server-rendered section for theme editor
        if (section.tagName === 'RECENTLY-VIEWED-PRODUCTS') {
          for (const attr of Array.from(section.attributes)) {
            if (attr.name === 'id') continue;
            this.setAttribute(attr.name, attr.value);
          }
          this.innerHTML = section.innerHTML;
        } else {
          const innerSection = section.querySelector('recently-viewed-products');
          if (innerSection) {
            for (const attr of Array.from(innerSection.attributes)) {
              if (attr.name === 'id') continue;
              this.setAttribute(attr.name, attr.value);
            }
            this.innerHTML = innerSection.innerHTML;
          } else {
            this.innerHTML = section.innerHTML;
          }
        }
        this.#applyTitlePriceLayout(this);
        this.#applyImageStyles(this);
        return true;
      }

      // Last resort: try to find any resource-list in the response
      const anyResourceList = fragment.querySelector('.resource-list');
      if (anyResourceList && anyResourceList.querySelectorAll('.resource-list__item').length > 0) {
        this.#productsLoaded = true;
        const sectionDiv = fragment.querySelector('.section-resource-list, .section');
        this.innerHTML = sectionDiv ? sectionDiv.innerHTML : anyResourceList.outerHTML;
        this.#applyTitlePriceLayout(this);
        this.#applyImageStyles(this);
        return true;
      }

      return false;
    } catch (error) {
      if (error.name !== 'AbortError' && !Shopify.designMode) {
        this.#handleError(error);
      }
      return false;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Fallback renderer: fetch product JSON and render simple cards
   * (useful when Section Rendering API can't resolve products server-side).
   * @param {string[]} handles
   * @param {string} layoutType
   * @returns {Promise<boolean>}
   */
  async #renderFallbackFromProductJson(handles, layoutType) {
    try {
      const uniqueHandles = [...new Set(handles)].slice(0, 10);
      const products = [];

      for (const handle of uniqueHandles) {
        try {
          const res = await fetch(`/products/${handle}.js`, {
            headers: { Accept: 'application/json' }
          });
          if (!res.ok) continue;
          const data = await res.json();
          if (!data?.handle) continue;
          products.push(data);
        } catch (e) {
          // ignore per-product failures
        }
      }

      if (!products.length) return false;

      const settings = this.#getSectionSettings();
      const avSettings = {
        avShowProductTypeWhenLessThanTwo: settings.avShowProductTypeWhenLessThanTwo,
        avHideLabelWhenLessThanTwo: settings.avHideLabelWhenLessThanTwo,
        avLabelSingular: settings.avLabelSingular,
        avLabelPlural: settings.avLabelPlural,
        avSuffix: settings.avSuffix
      };

      const itemsHtml = products
        .map((p) => {
          const url = `/products/${p.handle}`;
          const title = this.#escapeHtml(p.title || '');
          const image = Array.isArray(p.images) && p.images[0] ? p.images[0] : '';
          const priceText = formatPrice(typeof p.price === 'number' ? p.price : 0);

          const { html: availableVariantsHtml, visible: availableVariantsVisible } =
            this.#getAvailableVariantsForProduct(p, avSettings);
          const availableVariantsBlock = availableVariantsVisible && availableVariantsHtml
            ? `<div class="recently-viewed-products__fallback-available-variants text-block">${availableVariantsHtml}</div>`
            : '';

          return `
            <div class="resource-list__item">
              <div class="recently-viewed-products__fallback-card">
                <a class="recently-viewed-products__fallback-link" href="${url}">
                  ${image ? `<img class="recently-viewed-products__fallback-image" src="${image}" alt="${title}" loading="lazy">` : ''}
                  <div class="recently-viewed-products__fallback-content">
                    <div class="recently-viewed-products__fallback-title-price">
                      <div class="recently-viewed-products__fallback-title-block">
                        <div class="recently-viewed-products__fallback-title">${title}</div>
                        ${availableVariantsBlock}
                      </div>
                      <div class="recently-viewed-products__fallback-price">${this.#escapeHtml(priceText)}</div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          `.trim();
        })
        .join('');

      // Section visibility is controlled by data-has-products (theme CSS :has([data-has-products='false'])).

      // Find existing section markup
      const sectionDiv = this.querySelector('.section') || this.querySelector('div.section-resource-list');
      if (!sectionDiv) {
        this.innerHTML = `<div class="resource-list resource-list--${layoutType === 'carousel' ? 'carousel' : 'grid'}" data-has-products="true">${itemsHtml}</div>`;
        this.#productsLoaded = true;
        return true;
      }

      let resourceList = sectionDiv.querySelector('.resource-list');
      if (!resourceList) {
        resourceList = document.createElement('div');
        resourceList.className = `resource-list resource-list--${layoutType === 'carousel' ? 'carousel' : 'grid'}`;
        resourceList.setAttribute('data-has-products', 'true');
        sectionDiv.appendChild(resourceList);
      }

      resourceList.setAttribute('data-has-products', 'true');
      resourceList.className = `resource-list resource-list--${layoutType === 'carousel' ? 'carousel' : 'grid'}`;
      resourceList.innerHTML = itemsHtml;

      this.#productsLoaded = true;
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Enhances a minimal product card with image and title from product data
   * @param {string} cardHtml - The minimal product card HTML
   * @param {any} productData - The product JSON data
   * @returns {string} Enhanced product card HTML
   */
  #enhanceProductCard(cardHtml, productData) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cardHtml;

    const productCard = tempDiv.querySelector('product-card');
    if (!productCard) {
      return cardHtml;
    }

    const link = productCard.querySelector('a[href]');
    if (!link) {
      return cardHtml;
    }

    const settings = this.#getSectionSettings();
    const avSettings = {
      avShowProductTypeWhenLessThanTwo: settings.avShowProductTypeWhenLessThanTwo,
      avHideLabelWhenLessThanTwo: settings.avHideLabelWhenLessThanTwo,
      avLabelSingular: settings.avLabelSingular,
      avLabelPlural: settings.avLabelPlural,
      avSuffix: settings.avSuffix
    };
    const { html: availableVariantsHtml, visible: availableVariantsVisible } =
      this.#getAvailableVariantsForProduct(productData, avSettings);

    // Check if card already has image/gallery, title, and price
    const hasImage = productCard.querySelector('card-gallery, .product-card__gallery, img[src]');
    const hasContent = productCard.querySelector('.product-card__content');
    const hasTitle = productCard.querySelector('h3, .product-card__title, product-title');
    const hasPrice = productCard.querySelector('.product-card__price, [class*="price"]');
    const hasAvailableVariants = productCard.querySelector(
      '[data-block-type="available-variants"], .recently-viewed-products__enhanced-available-variants, .product-card__variant-count, swatches-variant-picker-component, variant-picker'
    );

    // Cards from other sections may show product type in [data-block-type="available-variants"] or .product-card__variant-count.
    // Enforce our section setting: show product type, count, or hide.
    const enforced = this.#getAvailableVariantsForProduct(productData, avSettings);
    const existingAvBlock = productCard.querySelector('[data-block-type="available-variants"]');
    const variantCountBlocks = productCard.querySelectorAll('.product-card__variant-count');
    const blocksToEnforce = [
      ...(existingAvBlock ? [existingAvBlock] : []),
      ...variantCountBlocks
    ];
    for (const el of blocksToEnforce) {
      if (!enforced.visible) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
        if (!enforced.preserveExistingContent && enforced.html) {
          el.innerHTML = enforced.html;
        }
        // When preserveExistingContent: show product type is on but JSON had no type – keep server-rendered content
      }
    }

    // Add image/gallery if missing - insert at the beginning of the link
    if (!hasImage && productData.images && productData.images.length > 0) {
      const imageUrl = productData.images[0];
      // Create a simple image wrapper that matches the theme structure
      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'product-card__image-wrapper recently-viewed-products__enhanced-image-wrapper';
      const productId = productData.id || productData.product_id || '';

      const imageElement = document.createElement('img');
      imageElement.src = imageUrl;
      imageElement.alt = productData.title || '';
      imageElement.loading = 'lazy';
      imageElement.className = 'product-card__image recently-viewed-products__enhanced-image';
      imageWrapper.appendChild(imageElement);
      link.insertBefore(imageWrapper, link.firstChild);
    }

    // Add product-card__content with title and price if missing
    // Only add if both content AND title are missing (to avoid duplicates)
    if (!hasContent && !hasTitle && productData.title) {
      const contentDiv = document.createElement('div');
      contentDiv.className = 'product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style gap-style';

      // Create a flex container for title and price (side by side)
      const titlePriceContainer = document.createElement('div');
      titlePriceContainer.className = 'product-card__title-price-container';

      const titleElement = document.createElement('h3');
      titleElement.className = 'product-card__title h4';
      titleElement.textContent = productData.title;

      const priceElement = document.createElement('div');
      priceElement.className = 'product-card__price';
      priceElement.textContent = formatPrice(typeof productData.price === 'number' ? productData.price : 0);

      const titleBlock = document.createElement('div');
      titleBlock.className = 'product-card__title-block';
      titleBlock.appendChild(titleElement);
      if (!hasAvailableVariants && availableVariantsVisible && availableVariantsHtml) {
        const avEl = document.createElement('div');
        avEl.className = 'recently-viewed-products__enhanced-available-variants text-block';
        avEl.innerHTML = availableVariantsHtml;
        titleBlock.appendChild(avEl);
      }
      titlePriceContainer.appendChild(titleBlock);
      titlePriceContainer.appendChild(priceElement);
      contentDiv.appendChild(titlePriceContainer);
      productCard.appendChild(contentDiv);
    } else if (hasContent && !hasTitle && productData.title) {
      const titlePriceContainer = document.createElement('div');
      titlePriceContainer.className = 'product-card__title-price-container';

      const titleElement = document.createElement('h3');
      titleElement.className = 'product-card__title h4';
      titleElement.textContent = productData.title;

      const priceElement = document.createElement('div');
      priceElement.className = 'product-card__price';
      priceElement.textContent = formatPrice(typeof productData.price === 'number' ? productData.price : 0);

      const titleBlock = document.createElement('div');
      titleBlock.className = 'product-card__title-block';
      titleBlock.appendChild(titleElement);
      if (!hasAvailableVariants && availableVariantsVisible && availableVariantsHtml) {
        const avEl = document.createElement('div');
        avEl.className = 'recently-viewed-products__enhanced-available-variants text-block';
        avEl.innerHTML = availableVariantsHtml;
        titleBlock.appendChild(avEl);
      }
      titlePriceContainer.appendChild(titleBlock);
      titlePriceContainer.appendChild(priceElement);
      hasContent.insertBefore(titlePriceContainer, hasContent.firstChild);
    } else if (hasTitle && !hasPrice && productData.title) {
      const titleElement = /** @type {HTMLElement | null} */ (productCard.querySelector('h3, .product-card__title, product-title'));
      if (titleElement) {
        const titlePriceContainer = document.createElement('div');
        titlePriceContainer.className = 'product-card__title-price-container';

        const priceElement = document.createElement('div');
        priceElement.className = 'product-card__price';
        priceElement.textContent = formatPrice(typeof productData.price === 'number' ? productData.price : 0);

        const titleBlock = document.createElement('div');
        titleBlock.className = 'product-card__title-block';
        titleElement.parentNode?.insertBefore(titlePriceContainer, titleElement);
        titlePriceContainer.appendChild(titleBlock);
        titleBlock.appendChild(titleElement);
        if (!hasAvailableVariants && availableVariantsVisible && availableVariantsHtml) {
          const avEl = document.createElement('div');
          avEl.className = 'recently-viewed-products__enhanced-available-variants text-block';
          avEl.innerHTML = availableVariantsHtml;
          titleBlock.appendChild(avEl);
        }
        titlePriceContainer.appendChild(priceElement);
      }
    } else if (hasTitle && hasPrice) {
      const titleElement = /** @type {HTMLElement | null} */ (productCard.querySelector('h3, .product-card__title, product-title'));
      if (titleElement && !titleElement.closest('.product-card__title-price-container')) {
        const titlePriceContainer = document.createElement('div');
        titlePriceContainer.className = 'product-card__title-price-container';

        const priceElement =
          /** @type {HTMLElement | null} */ (productCard.querySelector('product-price')) ||
          /** @type {HTMLElement | null} */ (productCard.querySelector('.product-card__price')) ||
          /** @type {HTMLElement | null} */ (productCard.querySelector('.price'));

        const titleBlock = document.createElement('div');
        titleBlock.className = 'product-card__title-block';
        titleElement.parentNode?.insertBefore(titlePriceContainer, titleElement);
        titlePriceContainer.appendChild(titleBlock);
        titleBlock.appendChild(titleElement);
        if (!hasAvailableVariants && availableVariantsVisible && availableVariantsHtml) {
          const avEl = document.createElement('div');
          avEl.className = 'recently-viewed-products__enhanced-available-variants text-block';
          avEl.innerHTML = availableVariantsHtml;
          titleBlock.appendChild(avEl);
        }
        if (priceElement && priceElement instanceof HTMLElement && priceElement.parentNode && priceElement.parentNode !== titlePriceContainer) {
          priceElement.parentNode.removeChild(priceElement);
          titlePriceContainer.appendChild(priceElement);
        }
      }
    }

    return productCard.outerHTML;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }

  /**
   * Applies image styles (padding and mix-blend-mode) to product images.
   *
   * @param {ParentNode} root - Root element to search within
   */
  #applyImageStyles(root = this) {
    const cards = root.querySelectorAll('product-card');
    for (const card of cards) {
      const images = card.querySelectorAll('card-gallery img, .card-gallery img, .product-card__image-wrapper img, .product-card__image, img[src]');
      for (const img of images) {
        if (img instanceof HTMLElement && !img.classList.contains('product-media__background')) {
          img.classList.add('recently-viewed-products__card-image-style');
        }
      }

      // Ensure card-gallery elements have a media-fit class for styling
      // Check data-media-fit attribute from server-render, or apply scale-down as default
      const cardGallery = card.querySelector('.card-gallery, card-gallery, slideshow-slide');
      if (cardGallery && cardGallery instanceof HTMLElement) {
        const mediaFitValue = cardGallery.getAttribute('data-media-fit') || 'scale-down';
        const hasMediaFitClass = cardGallery.classList.contains('media-fit-cover') ||
                                 cardGallery.classList.contains('media-fit-scale-down');
        if (!hasMediaFitClass) {
          // Apply the appropriate class based on data-media-fit attribute
          if (mediaFitValue === 'cover') {
            cardGallery.classList.add('media-fit-cover');
          } else {
            cardGallery.classList.add('media-fit-scale-down');
          }
        }
      }
    }
  }

  /**
   * Ensures each `product-card` shows title + price on one line:
   * - Title truncates with ellipsis
   * - Price stays on the right
   *
   * This is needed for server-rendered product cards (Section Rendering API),
   * where we don't run `#enhanceProductCard`.
   *
   * @param {ParentNode} root
   */
  #applyTitlePriceLayout(root = this) {
    const cards = root.querySelectorAll('product-card');
    for (const card of cards) {
      const titleEl = /** @type {HTMLElement | null} */ (
        card.querySelector('h3.product-card__title, .product-card__title, product-title')
      );
      if (!titleEl) continue;

      // Prefer moving the whole `product-price` component if it exists.
      const priceEl =
        /** @type {HTMLElement | null} */ (card.querySelector('product-price')) ||
        /** @type {HTMLElement | null} */ (card.querySelector('.product-card__price')) ||
        /** @type {HTMLElement | null} */ (card.querySelector('.price'));

      if (!priceEl) continue;

      /** @type {HTMLElement | null} */
      let container = titleEl.closest('.product-card__title-price-container');
      const priceContainer = priceEl.closest('.product-card__title-price-container');

      // Variant tag to wrap with title in a column block (variant below title).
      const variantEl = /** @type {HTMLElement | null} */ (
        card.querySelector('.product-card__variant-count, .recently-viewed-products__enhanced-available-variants, [data-block-type="available-variants"]')
      );

      // If not wrapped yet (or price/title in different containers), create wrapper and title-block (title + variant column).
      if (!container || (priceContainer && priceContainer !== container)) {
        container = document.createElement('div');
        container.className = 'product-card__title-price-container';
        titleEl.parentNode?.insertBefore(container, titleEl);

        const titleBlock = document.createElement('div');
        titleBlock.className = 'product-card__title-block';
        titleBlock.appendChild(titleEl);
        if (variantEl && variantEl.parentNode && variantEl.parentNode === container.parentNode) {
          variantEl.parentNode.removeChild(variantEl);
          titleBlock.appendChild(variantEl);
        }
        container.appendChild(titleBlock);
      } else if (container && !titleEl.closest('.product-card__title-block')) {
        // Container exists but title (and variant) are not inside a title-block: wrap them so variant sits below title.
        const titleBlock = document.createElement('div');
        titleBlock.className = 'product-card__title-block';
        container.insertBefore(titleBlock, titleEl);
        titleBlock.appendChild(titleEl);
        if (variantEl && variantEl.parentNode === container) {
          container.removeChild(variantEl);
          titleBlock.appendChild(variantEl);
        }
      } else if (container && variantEl) {
        // Title is already inside a title-block but variant is outside it (e.g. sibling of container): move variant into that title-block.
        const existingTitleBlock = titleEl.closest('.product-card__title-block');
        if (existingTitleBlock && !existingTitleBlock.contains(variantEl)) {
          variantEl.parentNode?.removeChild(variantEl);
          existingTitleBlock.appendChild(variantEl);
        }
      }

      // Layout and truncation are handled by CSS (recently-viewed-products .product-card__title-price-container and children).

      // If the price is nested under another wrapper, moving the wrapper is fine.
      // Ensure we don't append the same node twice.
      if (!priceEl.parentNode || priceEl.parentNode === container) continue;
      priceEl.parentNode.removeChild(priceEl);
      container.appendChild(priceEl); // Price goes second (after title block)
    }
  }

  /**
   * Renders the fetched products into the component
   * @param {Array<{id: string, html: string}>} productItems - Array of product HTML
   * @param {string} layoutType - Layout type (grid or carousel)
   * @param {string} carouselOnMobile - Whether to show carousel on mobile
   */
  async #renderProducts(productItems, layoutType, carouselOnMobile) {
    if (productItems.length === 0) {
      this.#handleEmptyState();
      return;
    }

    // Extract just the product-card content from each fetched product
    const productCards = [];
    for (const item of productItems) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = item.html;
      const productCard = tempDiv.querySelector('product-card, [data-product-id]');
      if (productCard) {
        productCards.push(productCard.outerHTML);
      }
    }

    if (productCards.length === 0) {
      this.#handleEmptyState();
      return;
    }

    // Create the resource list items HTML
    const itemsHtml = productCards
      .map((cardHtml, index) => {
        return `<div class="resource-list__item">${cardHtml}</div>${index < productCards.length - 1 ? '<!--@list/split-->' : ''}`;
      })
      .join('');

    // Find or create the resource list container
    const sectionDiv = this.querySelector('.section') || this.querySelector('div.section-resource-list');
    let resourceList = sectionDiv?.querySelector('.resource-list');

    if (!resourceList) {
      const resourceListDiv = document.createElement('div');
      resourceListDiv.className = `resource-list resource-list--${layoutType === 'carousel' ? 'carousel' : 'grid'}`;
      resourceListDiv.setAttribute('data-has-products', 'true');
      if (layoutType === 'grid') {
        resourceListDiv.setAttribute('data-testid', 'resource-list-grid');
      }
      resourceListDiv.innerHTML = itemsHtml;

      const contentArea = sectionDiv?.querySelector('.section-resource-list__content');
      if (contentArea) {
        contentArea.insertAdjacentElement('afterend', resourceListDiv);
      } else if (sectionDiv) {
        sectionDiv.appendChild(resourceListDiv);
      }
      resourceList = resourceListDiv;
    } else {
      resourceList.setAttribute('data-has-products', 'true');
      resourceList.className = `resource-list resource-list--${layoutType === 'carousel' ? 'carousel' : 'grid'}`;
      resourceList.innerHTML = itemsHtml;
    }

    this.#applyTitlePriceLayout(resourceList);
    this.#applyImageStyles(resourceList);
    this.#productsLoaded = true;
  }

  /**
   * Builds the section rendering URL with product IDs and handles
   * @param {string} sectionId - The section ID
   * @param {string[]} productIds - Array of product IDs
   * @param {string[]} productHandles - Array of product handles (may contain nulls)
   * @returns {string} The section rendering URL
   */
  #buildSectionUrl(sectionId, productIds, productHandles = []) {
    // Normalize section ID (remove shopify-section- prefix if present)
    const normalizedSectionId = normalizeSectionId(sectionId);
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', normalizedSectionId);
    url.searchParams.set('product_ids', productIds.join(','));
    // Add handles if available (filter out nulls)
    const validHandles = productHandles.filter((h) => h !== null && h !== undefined);
    if (validHandles.length > 0) {
      url.searchParams.set('product_handles', validHandles.join(','));
    }
    // Match theme section-renderer behavior for stable cache keys
    url.searchParams.sort();
    return url.toString();
  }

  /**
   * Gets the current product ID from the page (if on a product page)
   * @returns {string | null} The current product ID or null
   */
  /**
   * Gets the current product handle from the URL when on a product page (e.g. /products/foo → "foo").
   * @returns {string | null}
   */
  #getCurrentHandleFromUrl() {
    const match = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    const handle = match?.[1];
    return handle ? handle.trim() : null;
  }

  #getCurrentProductId() {
    // Prefer [data-product-id] that is NOT inside our own section (avoid picking up a card we rendered)
    const allWithId = document.querySelectorAll('[data-product-id]');
    let productForm = null;
    for (const el of allWithId) {
      if (!el.closest('recently-viewed-products')) {
        productForm = /** @type {HTMLElement} */ (el);
        break;
      }
    }
    if (productForm?.dataset?.productId) {
      return productForm.dataset.productId;
    }

    // Check for structured data in JSON-LD
    const structuredData = document.querySelector('script[type="application/ld+json"]');
    if (structuredData) {
      try {
        const data = JSON.parse(structuredData.textContent);
        if (data['@type'] === 'Product' && data.productID) {
          return data.productID.toString();
        }
        // Sometimes it's in a graph array
        if (Array.isArray(data['@graph'])) {
          const product = data['@graph'].find((item) => item['@type'] === 'Product');
          if (product?.productID) {
            return product.productID.toString();
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Fallback: check meta tags
    const metaProductId = document.querySelector('meta[property="product:id"], meta[name="product:id"]');
    if (metaProductId) {
      return metaProductId.getAttribute('content');
    }

    return null;
  }

  /**
   * Handles empty state when no products are available
   */
  #handleEmptyState() {
    this.#productsLoaded = true;
    const resourceList = this.querySelector('.resource-list');
    if (resourceList) {
      resourceList.setAttribute('data-has-products', 'false');
      resourceList.innerHTML = '';
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    if (!Shopify.designMode) {
      this.classList.add('hidden');
    }
    this.dataset.error = 'Error loading recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
